/**
 * Reseller Agreement tab (TMMPDM-0021): current contract + history; New version… /
 * Create agreement… opens the new-version page (TMMPDM-0036: `/partners/:id/agreement/new`).
 * TMMPDM-0037 (tmm-api 0099/0100): Revenue share is no longer shown (nothing reads it); the current
 * card and the history show the Official launch ("Not set" while absent).
 * TMMPDM-0089 (tmm-api 0154): the agreement no longer carries a rate card; it shows its currency (the
 * minimum's). The reseller's prices are the rate card it adopts (Pricing → Rate card).
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import {
  EditDetailsDialog,
  ErrorState,
  RowActionsMenu,
  fieldEditable,
  hasEditableFields,
} from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { adminApi } from '@/api/tmm/admin';
import type {
  ResellerAgreementList,
  ResellerAgreementNotesRequest,
  ResellerAgreementView,
} from '@/api/tmm/types';
import { Amount } from '@/components/ui';
import { formatDate } from '@/format';
import { TaxIdChip } from './TaxIdChip';

interface ResellerAgreementPanelProps {
  resellerId: number;
}

const METHOD_FALLBACK: Record<string, string> = {
  MANUAL: 'Manual',
  STRIPE_CONNECT_MANUAL: 'Stripe Connect (manual payouts)',
  STRIPE_CONNECT: 'Stripe Connect (automatic payouts)',
};

const CADENCE_FALLBACK: Record<string, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

const TAX_FALLBACK: Record<string, string> = {
  NOT_REQUIRED: 'Not required',
  PENDING: 'Pending',
  COLLECTED: 'Collected',
};

export function ResellerAgreementPanel({ resellerId }: ResellerAgreementPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [list, setList] = useState<ResellerAgreementList | null>(null);
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [detailsId, setDetailsId] = useState<number | null>(null);
  const [detailsToken, setDetailsToken] = useState(0);
  const agreementGeneration = useRef(0);
  const navigate = useNavigate();
  const { linkState } = useReturnTo();
  // The page's Cancel comes back to this tab.
  const openEditor = () =>
    void navigate(`/partners/${resellerId}/agreement/new`, { state: linkState });

  const refreshDetails = async (resetForm = true) => {
    const id = ++agreementGeneration.current;
    const row = await adminApi.getResellerAgreement(resellerId);
    if (agreementGeneration.current !== id) return;
    setList(row);
    setLoadFailure(null);
    if (resetForm) setDetailsToken((n) => n + 1);
  };

  useEffect(() => {
    const id = ++agreementGeneration.current;
    let cancelled = false;
    adminApi
      .getResellerAgreement(resellerId)
      .then((row) => {
        if (cancelled || agreementGeneration.current !== id) return;
        setList(row);
        setLoadFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled || agreementGeneration.current !== id) return;
        setList(null);
        setLoadFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, reloadToken]);

  if (loadFailure !== null && list === null) {
    return (
      <ErrorState
        error={loadFailure}
        message={t('resellers.agreement.loadError', 'Could not load the agreement.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  if (list === null) return <LoadingSpinner />;

  const current = list.current;
  const history = list.history;
  const detailsAgreement =
    [current, ...history].find((row) => row !== null && row.id === detailsId) ?? null;
  const currentHeading = t('resellers.agreement.current', 'Current agreement');

  return (
    <Box>
      {current === null ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" onClick={openEditor}>
              {t('resellers.agreement.create', 'Create agreement…')}
            </Button>
          }
        >
          {t(
            'resellers.agreement.none',
            "No agreement. This reseller's tenants can't sell until one exists.",
          )}
        </Alert>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                {currentHeading}
              </Typography>
              <RowActionsMenu
                name={currentHeading}
                items={
                  hasEditableFields(current.editableFields)
                    ? [
                        {
                          key: 'details',
                          label: t('components.editDetails.menu', 'Edit details…'),
                          onSelect: () => setDetailsId(current.id),
                        },
                      ]
                    : []
                }
              />
            </Stack>
            <Button variant="contained" onClick={openEditor}>
              {t('resellers.agreement.newVersion', 'New version…')}
            </Button>
          </Stack>
          <CurrentCard agreement={current} locale={locale} />
        </Stack>
      )}

      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('resellers.agreement.history', 'History')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('resellers.agreement.historyAria', 'Agreement history')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('resellers.agreement.col.effectiveFrom', 'Effective from')}</TableCell>
              <TableCell>{t('resellers.agreement.col.ended', 'Ended')}</TableCell>
              <TableCell>{t('resellers.agreement.col.currency', 'Currency')}</TableCell>
              <TableCell>
                {t('resellers.agreement.col.officialLaunch', 'Official launch')}
              </TableCell>
              <TableCell>{t('resellers.agreement.col.cadence', 'Cadence')}</TableCell>
              <TableCell>{t('resellers.agreement.col.monthlyMin', 'Monthly minimum')}</TableCell>
              <TableCell>{t('resellers.agreement.col.method', 'Method')}</TableCell>
              <TableCell>{t('resellerCards.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('resellers.agreement.historyEmpty', 'No previous versions.')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              history.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDate(row.effectiveFrom, locale)}</TableCell>
                  <TableCell>{formatDate(row.endedAt, locale)}</TableCell>
                  <TableCell>{row.currency}</TableCell>
                  <TableCell>
                    {row.officialLaunchOn !== undefined
                      ? formatDate(row.officialLaunchOn, locale)
                      : t('resellers.agreement.launchNotSet', 'Not set')}
                  </TableCell>
                  <TableCell>
                    {t(
                      `resellers.agreement.cadence.${row.payoutCadence}`,
                      CADENCE_FALLBACK[row.payoutCadence] ?? row.payoutCadence,
                    )}
                  </TableCell>
                  <TableCell>
                    <Amount
                      minor={row.monthlyMinimumMinor}
                      currency={row.currency}
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell>
                    {t(
                      `payouts.method.${row.payoutMethod}`,
                      METHOD_FALLBACK[row.payoutMethod] ?? row.payoutMethod,
                    )}
                  </TableCell>
                  <TableCell>
                    <RowActionsMenu
                      name={t('resellers.agreement.versionFrom', 'Agreement from {{date}}', {
                        date: formatDate(row.effectiveFrom, locale),
                      })}
                      items={
                        hasEditableFields(row.editableFields)
                          ? [
                              {
                                key: 'details',
                                label: t('components.editDetails.menu', 'Edit details…'),
                                onSelect: () => setDetailsId(row.id),
                              },
                            ]
                          : []
                      }
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <EditDetailsDialog
        open={detailsAgreement !== null}
        entityLabel={t('components.editDetails.entity.agreement', 'agreement')}
        fields={
          detailsAgreement && fieldEditable(detailsAgreement.editableFields, 'notes')
            ? [
                {
                  key: 'notes',
                  label: t('resellers.agreement.notes', 'Notes'),
                  value: detailsAgreement.notes ?? '',
                  kind: 'multiline',
                  maxLength: 500,
                },
              ]
            : []
        }
        version={detailsAgreement ? detailsAgreement.version : null}
        onClose={() => setDetailsId(null)}
        onReload={refreshDetails}
        onRefreshAfterSave={() => refreshDetails(false)}
        reloadToken={detailsToken}
        onSubmit={async (changed, version) => {
          const body: ResellerAgreementNotesRequest = {};
          if (changed.notes !== undefined) body.notes = changed.notes;
          if (version !== null) body.version = version;
          const saved = await adminApi.patchResellerAgreementNotes(resellerId, body);
          ++agreementGeneration.current;
          setList((current) =>
            current
              ? {
                  ...current,
                  current: current.current?.id === saved.id ? saved : current.current,
                  history: current.history.map((agreement) =>
                    agreement.id === saved.id ? saved : agreement,
                  ),
                }
              : current,
          );
        }}
      />
    </Box>
  );
}

function CurrentCard({ agreement, locale }: { agreement: ResellerAgreementView; locale: string }) {
  const { t } = useCopy();
  const currency = agreement.currency;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.currency', 'Currency')}:{' '}
          </Typography>
          {currency}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.officialLaunch', 'Official launch')}:{' '}
          </Typography>
          {agreement.officialLaunchOn !== undefined
            ? formatDate(agreement.officialLaunchOn, locale)
            : t(
                'resellers.agreement.launchNotSetLong',
                'Not set — no month is evaluated until one is set.',
              )}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.cadenceLabel', 'Payout cadence')}:{' '}
          </Typography>
          {t(
            `resellers.agreement.cadence.${agreement.payoutCadence}`,
            CADENCE_FALLBACK[agreement.payoutCadence] ?? agreement.payoutCadence,
          )}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.minPayout', 'Minimum payout')}:{' '}
          </Typography>
          <Amount minor={agreement.minPayoutMinor} currency={currency} locale={locale} />
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.monthlyMinimum', 'Monthly minimum')}:{' '}
          </Typography>
          <Amount minor={agreement.monthlyMinimumMinor} currency={currency} locale={locale} />
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.probation', 'Probation months')}:{' '}
          </Typography>
          {agreement.probationMonths}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'resellers.agreement.probationHint',
            "A month below the minimum opens probation; tmm flags the reseller for deactivation if it doesn't recover.",
          )}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.methodLabel', 'Payout method')}:{' '}
          </Typography>
          {t(
            `payouts.method.${agreement.payoutMethod}`,
            METHOD_FALLBACK[agreement.payoutMethod] ?? agreement.payoutMethod,
          )}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.taxDocsLabel', 'Tax documents')}:{' '}
          </Typography>
          {t(
            `resellers.agreement.taxDocs.${agreement.taxDocsStatus}`,
            TAX_FALLBACK[agreement.taxDocsStatus] ?? agreement.taxDocsStatus,
          )}
        </Typography>
        {/* TMMAPI-0161: shown as sent beside the tax documents; absent or null shows nothing. */}
        {agreement.taxIdStatus != null ? (
          <Typography variant="body2" component="div">
            <Typography component="span" color="text.secondary">
              {t('resellers.agreement.taxIdLabel', 'Tax ID')}:{' '}
            </Typography>
            <TaxIdChip status={agreement.taxIdStatus} />
          </Typography>
        ) : null}
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.connectTenant', 'Connect tenant')}:{' '}
          </Typography>
          {agreement.connectTenantSlug !== undefined ? (
            <Link
              component={RouterLink}
              to={`/partners/by-tenant/${encodeURIComponent(agreement.connectTenantSlug)}`}
              underline="always"
            >
              {agreement.connectTenantSlug}
            </Link>
          ) : (
            '—'
          )}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.payoutAccount', 'Payout account')}:{' '}
          </Typography>
          {agreement.payoutAccountRef ?? '—'}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.effectiveFrom', 'Effective from')}:{' '}
          </Typography>
          {formatDate(agreement.effectiveFrom, locale)}
        </Typography>
        <Typography variant="body2">
          <Typography component="span" color="text.secondary">
            {t('resellers.agreement.by', 'By')}:{' '}
          </Typography>
          {agreement.createdByAdmin}
        </Typography>
        {agreement.notes !== undefined ? (
          <Typography variant="body2">
            <Typography component="span" color="text.secondary">
              {t('resellers.agreement.notes', 'Notes')}:{' '}
            </Typography>
            {agreement.notes}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}
